import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Link as MuiLink } from "@mui/material";
import { makeStyles } from "components/providers/makeStyles";
import { VariableSizeList } from "react-window";

import { T } from "../../../util/t";
import { LoadingModalWithTrigger } from "../../heat/HeatModal";
import { HeatHeaderTitle } from "../../../../reducers/localization/translations";
import { NoSearchResults } from "../../../search/search";

const useSearchResultsStyles = makeStyles((theme, { padding }) => ({
    noResults: {
        padding,
    },
}));
export const EventSearchResults = ({
    eventId,
    loading,
    searchResults,
    clearSearch,
    maxHeight,
    padding = 0,
}) => {
    const classes = useSearchResultsStyles({ padding });
    const listRef = useRef();
    const { eventDivisions, competitors } = searchResults;

    const items = [...eventDivisions, ...competitors];
    const firstCompetitorIndex = eventDivisions.length;

    useEffect(() => {
        listRef.current &&
            listRef.current.resetAfterIndex(firstCompetitorIndex);
    }, [firstCompetitorIndex]);

    if (!loading && !items.length)
        return (
            <NoSearchResults
                className={classes.noResults}
                noResultsText={
                    <T>
                        Sorry, we can't seem to find any results for you search
                        😞
                    </T>
                }
                clearResultsText={<T>Clear your search and try again</T>}
                onClear={clearSearch}
            />
        );

    const headerHeight = 30;
    const itemHeight = 34;
    const itemCount = items.length;
    const height = Math.min(
        itemCount * itemHeight +
            (eventDivisions.length ? headerHeight : 0) +
            (competitors.length ? headerHeight : 0) +
            padding,
        maxHeight,
    );

    if (!items.length) return null;

    return (
        <VariableSizeList
            ref={listRef}
            estimatedItemSize={itemHeight}
            itemSize={(index) =>
                index === 0 || index === firstCompetitorIndex
                    ? headerHeight + itemHeight
                    : itemHeight
            }
            height={height}
            itemCount={itemCount}
            itemData={{ items, padding, eventId, firstCompetitorIndex }}
        >
            {EventSearchResultsItem}
        </VariableSizeList>
    );
};

const useStyles = makeStyles((theme, { padding }) => ({
    title: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: 700,
        textTransform: "uppercase",
        marginBottom: theme.spacing(2),
    },
    result: {
        display: "block",
        marginBottom: theme.spacing(2),
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
    },
    link: {
        cursor: "pointer",
        textDecoration: "underline",
    },
}));
export const EventSearchResultsItem = ({ index, style, data }) => {
    const classes = useStyles();
    const { items, padding, eventId, firstCompetitorIndex } = data;
    const item = items[index];

    return (
        <div
            key={`${item.__typename}-${item.id}`}
            style={{ ...style, padding }}
        >
            {item.__typename === "EventDivision" && (
                <>
                    {index === 0 && (
                        <header className={classes.title}>
                            <T>Divisions</T>
                        </header>
                    )}
                    <Link
                        className={classes.result}
                        to={`/events/${eventId}/divisions/${item.eventDivisionId || item.id}`}
                    >
                        {item.division.name}
                    </Link>
                </>
            )}
            {(item.__typename === "Competitor" ||
                item.__typename === "Entry") &&
                index === firstCompetitorIndex && (
                    <header className={classes.title}>
                        <T>Competitors</T>
                    </header>
                )}

            {item.__typename === "Entry" && (
                <MuiLink
                    component={Link}
                    variant="inherit"
                    underline="always"
                    className={classes.result}
                    to={`/events/${eventId}/divisions/${item.eventDivisionId}/entries`}
                >
                    {item.eventDivision.formatDefinition?.hideSeeds ||
                    !item.seed ? (
                        <T
                            athlete={item.athlete.name}
                            division={item.eventDivision.division.name}
                        >
                            entry_search_result_no_seed
                        </T>
                    ) : item.eventDivision.formatDefinition
                          ?.entriesRandomized ? (
                        <T
                            athlete={item.athlete.name}
                            division={item.eventDivision.division.name}
                            position={item.seed}
                        >
                            entry_search_result_with_position
                        </T>
                    ) : (
                        <T
                            athlete={item.athlete.name}
                            division={item.eventDivision.division.name}
                            seed={item.seed}
                        >
                            entry_search_result_with_seed
                        </T>
                    )}
                </MuiLink>
            )}

            {item.__typename === "Competitor" && (
                <div className={classes.result}>
                    <LoadingModalWithTrigger heat={item.heat}>
                        {item.athlete.name} <T>in</T>{" "}
                        <HeatHeaderTitle
                            className={classes.link}
                            heat={item.heat}
                        />
                    </LoadingModalWithTrigger>
                </div>
            )}
        </div>
    );
};
